import { Button } from "../../../components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip";
import { parseCAMTTransactions } from "../../../lib/xmlparser/xmlparser";
import { Logger } from "@/lib/logger/Logger";
import { supabase } from "@/lib/supabase";
import { SupabaseRpcEnum } from "@/lib/supabase/supabaseTypes";
import { showNotification } from "../../../store/Central/selectors";
import JSZip from "jszip";
import { ProcessingResultsDialog } from "./processing-results-dialog.component";
import { TableInvoiceRow, ProcessedTransaction } from "../types/invoice-table.types";
import { useState } from "react";
import { useRemindersStore } from "../../../store/Reminders";

interface FileProcessingResult {
	fileName: string;
	success: boolean;
	transactionCount: number;
	transactions: ProcessedTransaction[];
	error?: string;
}

interface Camt54UploadProps {
	organizationId: string | undefined;
	accountingData: TableInvoiceRow[];
	onProcessingComplete: () => Promise<void>;
	disabled?: boolean;
	className?: string;
}

export const Camt54Upload: React.FC<Camt54UploadProps> = ({
	organizationId,
	accountingData,
	onProcessingComplete,
	disabled = false,
	className = "",
}) => {
	const [processingFiles, setProcessingFiles] = useState<boolean>(false);
	const [processedTransactions, setProcessedTransactions] = useState<ProcessedTransaction[]>([]);
	const [resultsDialogOpen, setResultsDialogOpen] = useState<boolean>(false);

	const isXMLFile = (fileName: string): boolean => {
		const lowerFileName = fileName.toLowerCase();
		return lowerFileName.endsWith('.xml')
	};

	const extractFilesFromZip = async (zipFile: File): Promise<File[]> => {
		try {
			const zip = new JSZip();
			const zipContent = await zip.loadAsync(zipFile);
			const extractedFiles: File[] = [];

			for (const [fileName, zipEntry] of Object.entries(zipContent.files)) {
				if (!zipEntry.dir && isXMLFile(fileName)) {
					const fileContent = await zipEntry.async('blob');
					const file = new File([fileContent], fileName, { type: 'application/xml' });
					extractedFiles.push(file);
				}
			}

			return extractedFiles;
		} catch (error) {
			Logger.error(error);
			return [];
		}
	};

	const processSingleFile = async (file: File): Promise<FileProcessingResult> => {
		try {
			const content = await file.text();
			const transactions = await parseCAMTTransactions(content);

			if (!organizationId) {
				throw new Error("Keine Organisation gefunden");
			}

			const { data, error } = await supabase.rpc(
				SupabaseRpcEnum.PROCESS_CAMT54_TRANSACTIONS,
				{
					organization_id: organizationId,
					transactions: transactions.map((ts) => ({
						Amount: ts.Amount,
						AmountCurrency: ts.AmountCurrency,
						RemittanceInformation: ts.RemittanceInformation,
						ValueDate: ts.ValueDate,
						BookingDate: ts.BookingDate,
					})),
				}
			);

			if (error) {
				throw new Error(`Datenbankfehler: ${error.message}`);
			}
			useRemindersStore.getState().fetchDueCount();

			// Parse the new JSON response format: array of {invoice_id, status, doc_number}
			const results = data as Array<{ invoice_id: number | null; status: 'success' | 'duplicate' | 'not_found'; doc_number: string }>;

			// Extract invoice IDs that were successfully processed or are duplicates (to fetch doc_numbers)
			const invoiceIds = results
				.filter(r => r.invoice_id !== null)
				.map(r => r.invoice_id as number);

			// Fetch invoice doc_numbers for the processed invoices
			const { data: invoicesData, error: invoicesError } = await supabase
				.from('invoices')
				.select('id, doc_number')
				.in('id', invoiceIds);

			if (invoicesError) {
				Logger.error('Failed to fetch invoice doc_numbers:'+ invoicesError);
			}

			// Create a map of invoice_id to doc_number
			const invoiceMap = new Map(
				invoicesData?.map(inv => [inv.id, inv.doc_number]) || []
			);

			// Build ProcessedTransaction objects
			const processedTransactions: ProcessedTransaction[] = transactions.map((ts, index) => {
				const result = results[index];
				const beleg = ts.RemittanceInformation.substring(20, 26);
				const invoiceId = result.invoice_id;
				const invoiceData = accountingData.find(inv => inv.invoice_id === invoiceId);

				let clientName = 'Unbekannt';
					if (invoiceData?.recipient_is_company) {
						clientName = invoiceData?.recipient_company_name || 'Unbekannt';
					} else if (invoiceData?.recipient_first_name || invoiceData?.recipient_last_name) {
						clientName = `${invoiceData?.recipient_first_name || ''} ${invoiceData?.recipient_last_name || ''}`.trim();
					}


				return {
					invoice_id: invoiceId,
					invoice_doc_number: invoiceId ? invoiceMap.get(invoiceId) || beleg : beleg,
					invoice_date: invoiceData?.invoice_date || null,
					client_name: clientName,
					amount: ts.Amount,
					currency: ts.AmountCurrency,
					valuta_date: ts.ValueDate,
					booking_date: ts.BookingDate,
					remittance_information: ts.RemittanceInformation,
					success: result.status === 'success' || result.status === 'duplicate',
					error: result.status === 'not_found' ? 'Rechnung nicht gefunden' : undefined,
					file_name: file.name,
					already_processed: result.status === 'duplicate',
				};
			});

			return {
				fileName: file.name,
				success: true,
				transactionCount: transactions.length || 0,
				transactions: processedTransactions,
			};
		} catch (error) {
			Logger.error(error);
			return {
				fileName: file.name,
				success: false,
				transactionCount: 0,
				transactions: [],
				error: error instanceof Error ? error.message : "Unbekannter Fehler",
			};
		}
	};

	const parseAndProcessCAMT54Transactions = async (files: File[]) => {
		if (!organizationId) {
			showNotification({
				message: "Keine Organisation gefunden",
				type: "error",
			});
			Logger.error("No organization id found");
			return;
		}

		setProcessingFiles(true);
		showNotification({
			message: `${files.length} CAMT54 Dateien werden verarbeitet...`,
			type: "info",
		});

		const results: FileProcessingResult[] = [];
		const allProcessedTransactions: ProcessedTransaction[] = [];
		let totalTransactions = 0;
		let successfulFiles = 0;
		let failedFiles = 0;

		for (const file of files) {
			const result = await processSingleFile(file);
			results.push(result);

			if (result.success) {
				successfulFiles++;
				totalTransactions += result.transactionCount;
				allProcessedTransactions.push(...result.transactions);
			} else {
				failedFiles++;
			}
		}

		// Refresh accounting data first
		await onProcessingComplete();

		// Store enriched transactions in state
		setProcessedTransactions(allProcessedTransactions);

		// Show summary notification
		if (successfulFiles > 0) {
			showNotification({
				message: `${successfulFiles} Dateien erfolgreich verarbeitet mit ${totalTransactions} Transaktionen`,
				type: "success",
			});
			if(failedFiles > 0) {
				showNotification({
					message: `${failedFiles} Dateien fehlgeschlagen`,
					type: "error",
				});
			}

			// Open results dialog
			setResultsDialogOpen(true);
		} else {
			showNotification({
				message: "Keine Dateien konnten verarbeitet werden",
				type: "error",
			});
		}

		// Log detailed results
		Logger.info("CAMT54 processing results", { results, totalTransactions });

		setProcessingFiles(false);
	};

	const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		setProcessedTransactions([]);
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		const allFiles: File[] = [];

		try {
			for (const file of files) {
				if (file.type === 'application/zip' || file.name.toLowerCase().endsWith('.zip')) {
					// Handle zip files
					const extractedFiles = await extractFilesFromZip(file);
					allFiles.push(...extractedFiles);
				} else if (isXMLFile(file.name)) {
					// Handle individual CAMT54 files
					allFiles.push(file);
				}
			}

			if (allFiles.length === 0) {
				showNotification({
					message: "Keine CAMT54 Dateien in den ausgewählten Dateien gefunden",
					type: "warning",
				});
				return;
			}

			await parseAndProcessCAMT54Transactions(allFiles);
		} catch (error) {
			Logger.error(error);
			showNotification({
				message: error instanceof Error ? error.message : "Fehler beim Verarbeiten der Dateien",
				type: "error",
			});
		} finally {
			// Reset file input
			e.target.value = '';
		}
	};

	return (
		<>
			<input
				type="file"
				className="hidden"
				id="camt54-upload"
				multiple
				accept=".xml,.zip"
				onChange={handleFileUpload}
				disabled={processingFiles || disabled}
			/>
			{processedTransactions.length > 0 && (
				<Button
					variant="outline"
					disabled={processingFiles}
					onClick={() => setResultsDialogOpen(true)}
					data-testid="camt54-show-results"
					aria-label="CAMT54 Verarbeitungsdetails anzeigen"
				>
					Verarbeitungsdetails anzeigen ({processedTransactions.length})
				</Button>
			)}
			<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="default"
							disabled={processingFiles || disabled}
							className={className}
							data-testid="camt54-upload-button"
							aria-label="CAMT54 Dateien hochladen"
							asChild
						>
							<label
								className={`cursor-pointer ${processingFiles || disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
								htmlFor="camt54-upload"
							>
								{processingFiles ? "Verarbeite..." : "CAMT54 Hochladen"}
							</label>
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						Elektronische Kontoauszüge (CAMT54) zur automatischen Zahlungserfassung hochladen. Es können mehrere ZIP und XML-Dateien gleichzeitig erfasst werden.
					</TooltipContent>
			</Tooltip>

			<ProcessingResultsDialog
				open={resultsDialogOpen}
				onClose={() => setResultsDialogOpen(false)}
				transactions={processedTransactions}
				mode="payment"
			/>
		</>
	);
};
